import { useDebouncedEffect } from '@timelyapp/react';
import { useState } from 'react';

export interface DebouncedValue<T> {
  /** The settled value: what `value` was when it last stopped changing for the delay. */
  value: T;
  /**
   * True while `value` has moved on and the settled copy has not caught up. An answer derived from
   * the settled copy is stale while this holds — it answers an older question than the one asked.
   */
  isPending: boolean;
}

/**
 * Returns `value` delayed until it has stopped changing for `delayMs`, and whether that delay is
 * currently running.
 *
 * The first value is returned undelayed (`runOnMount: false`): a component that mounts with a
 * value already set must not spend a delay before it can use it.
 *
 * The comparison is a plain identity check on the values it was handed. A caller whose input has
 * two spellings of "nothing" (`undefined` and `''`) must collapse them BEFORE calling, or a
 * transition between the two opens a delay window nothing was typed into.
 */
export function useDebouncedValue<T>(value: T, delayMs: number): DebouncedValue<T> {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useDebouncedEffect(() => setDebouncedValue(value), [value], {
    delay: delayMs,
    runOnMount: false,
  });

  return { value: debouncedValue, isPending: debouncedValue !== value };
}
